import React, { useEffect, useState, Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Truck, CheckCircle2, ArrowRight, ChevronRight, Phone, Calculator as CalculatorIcon } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceCity, getServiceProduct } from '../data/cityMaterialContent';
import { NotFound } from './NotFound';
// Delivery tiers collapsed to three rings: inner (former zones 1+2 → +$80),
// middle (zone 3 → +$120, unchanged), outer (former zones 4+5 → +$175).
// Curated city.zone values (1–5) still map cleanly onto the right tier fee.
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
function ProjectFitForMaterial(material: string): string[] {
  const m = material.toLowerCase();
  if (m.includes('topsoil') || m.includes('compost')) return ['New lawn install or overseeding', 'Raised beds and garden plots', 'Leveling low spots in the yard'];
  if (m.includes('bark') || m.includes('mulch')) return ['Refreshing flower beds and tree rings', 'Pathways and play areas', 'Reducing weeds and retaining moisture'];
  if (m.includes('drain')) return ['French drains and footing drains', 'Behind retaining walls', 'Crushed rock alternatives where drainage matters'];
  if (m.includes('minus') || m.includes('crushed')) return ['Driveways and parking pads', 'Compacted base for patios and walkways', 'General-purpose road base'];
  if (m.includes('pea gravel')) return ['Patios, walkways, and dog runs', 'Pool decks and play areas', 'Decorative groundcover'];
  return ['Driveway base and resurfacing', 'General landscape and grading', 'Filling and grading project sites'];
}
// Tabbed "Explore" panel — replaces what used to be two stacked grid
// sections on the page. Renders the same links (SEO unchanged) in one
// shorter block that lets users switch between "other materials in this
// city" and "this material in other cities" with a single tap.
function ExploreBlock({
  city,
  product,
  otherMaterials,
  otherCities
}: {
  city: any;
  product: any;
  otherMaterials: {
    product: any;
    category: any;
  }[];
  otherCities: any[];
}) {
  const [tab, setTab] = useState<'materials' | 'cities'>('materials');
  return <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2354" data-mp="1m29sdto0ewvd">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6" data-id="element-2355" data-mp="1h3p2oo6dvvl6">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2356" data-mp="1cbnga9fi4ohf">
          Explore
        </h2>
        <div className="inline-flex rounded-xl bg-stone-100 dark:bg-white/[0.04] p-1 border border-stone-200 dark:border-white/10" role="tablist" data-id="element-2357" data-mp="17d3uc90wrcp2">
          <button type="button" role="tab" aria-selected={tab === 'materials'} onClick={() => setTab('materials')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'materials' ? 'bg-white dark:bg-white/10 text-brand-orange shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-2358" data-mp="ycns12vwibls">
            Other materials in {city.name}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'cities'} onClick={() => setTab('cities')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'cities' ? 'bg-white dark:bg-white/10 text-brand-orange shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-2359" data-mp="3e25up23rqc6g">
            {product.name} in other cities
          </button>
        </div>
      </div>

      {tab === 'materials' ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2360" data-mp="2v8sa9qxolo53">
          {otherMaterials.map(({
        product: p
      }) => <Link key={p.slug} to={`/gravel-delivery/${city.slug}/${p.slug}`} className="group p-5 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2361" data-mp="22mh8icyc9h6p">
              <div className="flex items-start justify-between gap-3" data-id="element-2362" data-mp="e33c85m6k97h">
                <div data-id="element-2363" data-mp="93qlh9s6uhzm">
                  <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2364" data-mp="94i83v3t55dr">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-xs text-stone-500 dark:text-stone-400" data-id="element-2365" data-mp="i33objlt48bd">
                    ${p.pricePerYard}/yd · Delivered to {city.name}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-brand-orange group-hover:translate-x-0.5 transition-all shrink-0" data-id="element-2366" data-mp="rsn9ivwzse9e" />
              </div>
            </Link>)}
        </div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-2367" data-mp="11rmfg6qdhvsp">
          {otherCities.map(c => <Link key={c.slug} to={`/gravel-delivery/${c.slug}/${product.slug}`} className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2368" data-mp="37lvx45v795ks">
              <span className="text-sm font-bold text-stone-800 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2369" data-mp="2xhfj7i1a5yjm">
                {c.name}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-2370" data-mp="2oom0tbqjej1j" />
            </Link>)}
        </div>}
    </section>;
}
export function CityMaterial() {
  const {
    citySlug = '',
    materialSlug = ''
  } = useParams<{
    citySlug: string;
    materialSlug: string;
  }>();
  const city = getServiceCity(citySlug);
  const productEntry = getServiceProduct(materialSlug);
  if (!city || !productEntry) return <NotFound data-id="element-2371" data-mp="25qj5kf7ebpva" />;
  const {
    product,
    category
  } = productEntry;
  const zoneFee = ZONE_FEE[city.zone] || 'Custom Quote';
  const projectFits = ProjectFitForMaterial(product.name);
  // Other materials in this city — exclude the current one.
  const otherMaterials = SERVICE_PRODUCT_SLUGS.filter(s => s !== materialSlug).map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[];
  // Other cities for this material — exclude the current one.
  const otherCities = SERVICE_CITIES.filter(c => c.slug !== city.slug);
  // Build the estimator deep link — preselects address, material, and the
  // standard full-load yardage so the user lands on a ready-to-quote form.
  const estimatorHref = `/?address=${encodeURIComponent(city.geocodeHint)}&material=${encodeURIComponent(product.name)}&qty=12#estimator`;
  const heroTitle = `${product.name} Delivery in ${city.name}, ${city.state}`;
  // Set page title for SEO (HashRouter still allows document.title updates).
  useEffect(() => {
    const original = document.title;
    document.title = `${product.name} in ${city.name}, WA — 206Gravel Same-Day Delivery`;
    return () => {
      document.title = original;
    };
  }, [product.name, city.name]);
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2372" data-mp="16impl1b3fwl2">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-2373" data-mp="16kzgw52qrjas">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-2374" data-mp="1bjj4e9wzh359">
          <li data-id="element-2375" data-mp="1givqutjy087b">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-2376" data-mp="1lfvxp6om9ezh">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2377" data-mp="1pi8zxl45muri" />
          <li data-id="element-2378" data-mp="1pfef3sh8b7xk">
            <Link to="/service-areas" className="hover:text-brand-orange transition-colors" data-id="element-2379" data-mp="1ugkajy29x9as">
              Service Areas
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2380" data-mp="1z8li56ohybuh" />
          <li data-id="element-2381" data-mp="246dkyvqojidg">
            <Link to={`/service-areas#${city.slug}`} className="hover:text-brand-orange transition-colors" data-id="element-2382" data-mp="246wsrjmm8qdr">
              {city.name}
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2383" data-mp="293dj8khafgu0" />
          <li className="text-stone-900 dark:text-white font-semibold truncate" aria-current="page" data-id="element-2384" data-mp="2d6a64ndv1z3r">
            {product.name}
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12" data-id="element-2385" data-mp="2ugy5q57my2mo">
        <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-2386" data-mp="38g6816z3hqv3">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-2387" data-mp="38feoiuirvjl6">
            <div className="inline-flex items-center gap-2 mb-3" data-id="element-2388" data-mp="2fs200et1x1p8">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-2389" data-mp="2at77um9720sg" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-2390" data-mp="2aql4kytc67g1">
                {city.name}, {city.state} · {city.county} County · Zone{' '}
                {city.zone}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-2391" data-mp="1wzzgmvp0z7ba">
              {heroTitle}
            </h1>
            <p className="mt-4 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2392" data-mp="7jnioud5043x">
              {city.blurb} Order {product.name.toLowerCase()} by the cubic yard
              with same-day or next-day delivery to {city.name} and surrounding
              neighborhoods including{' '}
              {city.neighborhoods.slice(0, 3).join(', ')}, and{' '}
              {city.neighborhoods[city.neighborhoods.length - 1]}.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2393" data-mp="qb6kixvfdc4d">
              <Link to={estimatorHref} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2394" data-mp="v8ey0l5morza">
                <CalculatorIcon className="w-4 h-4" data-id="element-2395" data-mp="m6ytgu1hvk2z" />
                Get an Instant Estimate
                <ArrowRight className="w-4 h-4" data-id="element-2396" data-mp="31ymmwfhauuh1" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-stone-300 dark:border-white/15 text-stone-900 dark:text-white font-bold text-sm hover:bg-stone-100 dark:hover:bg-white/5 transition-colors" data-id="element-2397" data-mp="370257zs3emvq">
                <Phone className="w-4 h-4" data-id="element-2398" data-mp="1uet4jpa2sjzq" />
                (206) 619-3742
              </a>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-xs" data-id="element-2399" data-mp="1zdmh9fhjg6yu">
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2400" data-mp="1zbrx43e3civt">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2401" data-mp="1pfggj17gx6vk">
                  Price
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2402" data-mp="1bi08xmdfr728">
                  ${product.pricePerYard}/yd
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2403" data-mp="35v9pfy4efstz">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2404" data-mp="3aqyrq97szntz">
                  Delivery
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2405" data-mp="3fz6j2yl73zdy">
                  {zoneFee}
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2406" data-mp="3oylac096sdzi">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2407" data-mp="7hah8d0npiyi">
                  Lead Time
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2408" data-mp="girr3dnh0oer">
                  Same-day*
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative rounded-2xl overflow-hidden border border-stone-200 dark:border-white/10 bg-white dark:bg-white/[0.04] aspect-[4/3]" data-id="element-2409" data-mp="cfbycseiv87c">
            {product.image ? <img src={product.image} alt={`${product.name} delivered in ${city.name}, WA`} className="w-full h-full object-cover" loading="eager" data-id="element-2410" data-mp="m78k6mpu5zfo" /> : <div className="w-full h-full bg-stone-100 dark:bg-stone-800" data-id="element-2411" data-mp="w563qwwr381y" />}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between" data-id="element-2412" data-mp="w337hv508ios">
              <span className="px-3 py-1 rounded-full bg-black/70 backdrop-blur-md text-white text-[11px] font-bold" data-id="element-2413" data-mp="104oqzzc1nv7y">
                {category.name}
              </span>
              <Link to={`/shop/${product.slug}`} className="px-3 py-1 rounded-full bg-brand-orange text-white text-[11px] font-bold hover:bg-brand-orange-hover transition-colors" data-id="element-2414" data-mp="19xvx5melzf93">
                View Material
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What it's for */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2415" data-mp="1nu9e8uf8den1">
        <div className="grid md:grid-cols-3 gap-8" data-id="element-2416" data-mp="34csz7y8vdma6">
          <div className="md:col-span-1" data-id="element-2417" data-mp="34b81y0v67p8m">
            <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2418" data-mp="2zdoqf8puxqkd">
              What {city.name} customers use{' '}
              <span className="text-brand-orange" data-id="element-2419" data-mp="2ugg3yvxof0db">{product.name}</span> for
            </h2>
            <p className="mt-3 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2420" data-mp="3s1uadt937bj5">
              {product.description}
            </p>
          </div>
          <div className="md:col-span-2" data-id="element-2421" data-mp="3ic0xmox7kqdb">
            <ul className="space-y-3" data-id="element-2422" data-mp="3iazncmo0o7wn">
              {projectFits.map(fit => <li key={fit} className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2423" data-mp="3de979zc7pcb8">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-2424" data-mp="epk1u94fr1jq" />
                  <span className="text-sm text-stone-800 dark:text-stone-200 font-medium" data-id="element-2425" data-mp="9vfu3nnciphl">
                    {fit}
                  </span>
                </li>)}
              {product.bestFor && product.bestFor.length > 0 && <li className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2426" data-mp="3ujdx41oanrxy">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-2427" data-mp="3lm3l2e3njyrz" />
                  <span className="text-sm text-stone-800 dark:text-stone-200 font-medium" data-id="element-2428" data-mp="d4pcqhee5igr">
                    Also great for: {product.bestFor.join(', ')}
                  </span>
                </li>}
            </ul>
          </div>
        </div>
      </section>

      {/* Delivery to this city */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2429" data-mp="8dz1won74neo">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-6" data-id="element-2430" data-mp="w31tao7mtumd">
          Delivering to {city.name}, {city.state}
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-2431" data-mp="2lkoxqx4cojoj">
          <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2432" data-mp="2lj455d1ljatb">
            <div className="flex items-center gap-3 mb-3" data-id="element-2433" data-mp="2udiu67bxhzpm">
              <div className="w-10 h-10 rounded-full bg-brand-orange/10 flex items-center justify-center" data-id="element-2434" data-mp="2ud0q4oj0yh75">
                <Truck className="w-5 h-5 text-brand-orange" data-id="element-2435" data-mp="2qcqk5bv8fqsa" />
              </div>
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white" data-id="element-2436" data-mp="22n5phvppv536">
                Zone {city.zone} — {zoneFee} delivery
              </h3>
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2437" data-mp="2bnaszurshjc2">
              {city.name} sits in our Zone {city.zone} delivery area. Our
              standard {city.zone === 1 ? 'inner-zone' : 'core-zone'} rate
              applies — billed per truckload, not per yard.
            </p>
            <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-2438" data-mp="3i2bhjfehiz8m">
              See {city.name} on the zone map
              <ArrowRight className="w-3 h-3" data-id="element-2439" data-mp="2kqmj2gp1zmoh" />
            </Link>
          </div>
          <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2440" data-mp="2or6dvdm5me7g">
            <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-2441" data-mp="2tn3u7jxf3kk8">
              Neighborhoods we cover
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-2442" data-mp="211u6mjae0g7s">
              {city.neighborhoods.map(n => <span key={n} className="px-3 py-1 rounded-full bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 text-xs font-medium text-stone-700 dark:text-stone-300" data-id="element-2443" data-mp="25zdl9kazyv09">
                  {n}
                </span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Explore — single tabbed block that combines "other materials" and
                    "other cities" so the page doesn't feel like a sitemap of stacked
                    identical link grids. Same SEO links, half the scroll. */}
      <ExploreBlock city={city} product={product} otherMaterials={otherMaterials} otherCities={otherCities} data-id="element-2444" data-mp="15si9gm501xpx" />

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-stone-200 dark:border-white/10" data-id="element-2445" data-mp="wr0vpaf2e84e">
        <div className="p-8 md:p-10 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 dark:border-white/10 text-center" data-id="element-2446" data-mp="rsfrz10lt6b7">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-2447" data-mp="mzmrwqg2ym5g">
            Ready to schedule {product.name} delivery to {city.name}?
          </h2>
          <p className="mt-3 text-stone-300 max-w-2xl mx-auto text-sm md:text-base" data-id="element-2448" data-mp="d4cn5uyas0nz">
            Get an instant estimate — no signup, no calls required. Or call
            dispatch directly to skip the form.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-id="element-2449" data-mp="43506918em97">
            <Link to={estimatorHref} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-2450" data-mp="1rzkp61jzm6ek">
              <CalculatorIcon className="w-4 h-4" data-id="element-2451" data-mp="1ixl8598z32bm" />
              Get Instant Estimate
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-2452" data-mp="1mydgm0a0t36u">
              <Phone className="w-4 h-4" data-id="element-2453" data-mp="1e0bjvwxbkwpd" />
              (206) 619-3742
            </a>
          </div>
          <p className="mt-5 text-[11px] text-stone-500" data-id="element-2454" data-mp="zbf39bh76bd5">
            *Same-day delivery subject to truck availability. Standard hours
            Mon–Sat.
          </p>
        </div>
      </section>
    </main>;
}